import { useLingui } from '@lingui/react/macro'
import { Input } from 'designSystem/components/Input'
import { Text } from 'designSystem/components/Text'
import {
  MetaLabel,
  MetaList,
  MetaRow,
  MetaValue,
} from 'features/knowledgeDocumentViewer/components/metadata/metadataFieldListStyles'
import { metadataFieldLabelMessage } from 'features/knowledgeDocumentViewer/utils/metadataFieldLabelMessages'
import type { MetadataDisplayField } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import React, { useCallback } from 'react'

export type EditableMetadataFieldListProps = {
  fields: readonly MetadataDisplayField[]
  disabled?: boolean
  lockedLabels?: ReadonlySet<string>
  onFieldChange: (label: string, value: string) => void
}

export function EditableMetadataFieldList({
  fields,
  disabled = false,
  lockedLabels,
  onFieldChange,
}: EditableMetadataFieldListProps): React.ReactElement | null {
  const { i18n } = useLingui()
  const handleChange = useCallback(
    (label: string, value: string) => {
      onFieldChange(label, value)
    },
    [onFieldChange]
  )

  if (fields.length === 0) return null

  return (
    <MetaList>
      {fields.map(({ label, value }) => {
        const isLocked = lockedLabels?.has(label) === true
        const displayLabel = i18n._(metadataFieldLabelMessage(label))
        return (
          <MetaRow key={label}>
            <MetaLabel>
              <Text variant='body' color='muted'>
                {displayLabel}
              </Text>
            </MetaLabel>
            <MetaValue>
              {isLocked ? (
                <Text variant='body' color='muted'>
                  {value || '—'}
                </Text>
              ) : (
                <Input
                  inputSize='sm'
                  value={value}
                  disabled={disabled}
                  aria-label={displayLabel}
                  onChange={(event) =>
                    handleChange(label, event.currentTarget.value)
                  }
                />
              )}
            </MetaValue>
          </MetaRow>
        )
      })}
    </MetaList>
  )
}
